import { shouldAnimate, transition } from '@helpers/animate';
import { imageCoverContainer } from '@helpers/aspectRatio';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { baseGrid } from '@helpers/grid';
import { Device, between, from, until } from '@helpers/media';
import scrollbars from '@helpers/scrollbars';
import { contentStyles, h2 } from '@helpers/typography';
import svgCurveReverse from '@img/icons/background/mep-cta-curve-reverse.svg?url';
import svgCurve from '@img/icons/background/mep-cta-curve.svg?url';
import svgSwooshReverse from '@img/icons/background/mep-cta-swoosh-reverse.svg?url';
import svgSwoosh from '@img/icons/background/mep-cta-swoosh.svg?url';
import mepAdditionalBg from '@img/mep-cta-additional.png';
import ButtonStyles from '@stories/Components/Global/Buttons/Button/Button.styles';
import SectionWrapper from '@stories/Components/Global/SectionWrapper/SectionWrapper';
import SectionWrapperStyles from '@stories/Components/Global/SectionWrapper/SectionWrapper.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const Container = styled(SectionWrapper) `
  --highlightColour: currentColor;

  overflow: hidden;
  background-color: var(--bgColour);
  position: relative;

  &[data-site='ess'] {
    --textColour: ${brand.ess.primary};
    --titleColour: ${brand.ess.primary};
    --highlightColour: ${brand.global.red};

    &[data-variant='secondary'] {
      overflow: visible;

      ${SectionWrapperStyles.Inner} {
        overflow: hidden;
      }
    }

    &[data-variant='tertiary'] {
      --bgColour: ${brand.ess.secondary.lightestGrey};

      ${SectionWrapperStyles.Inner} {
        padding-top: 0;
        padding-bottom: 0;
      }
    }

    @media ${from(Device.TabletLarge)} {
      ${SectionWrapperStyles.Inner} {
        padding-bottom: 0;
      }

      &[data-variant='primary'] {
        ${SectionWrapperStyles.Inner} {
          position: relative;

          &::before {
            position: absolute;
            bottom: calc(var(--sitePadding) * 1);
            left: calc(var(--sitePadding) * -1);
            content: '';
            display: block;
            width: calc(100% + var(--sitePadding) * 2);
            height: 1.5px;
            background-color: ${brand.ess.secondary.midGrey};
            background-repeat: no-repeat;
            background-position: top;
          }
        }
      }

      &[data-variant='secondary'] ${SectionWrapperStyles.Inner} {
        margin-top: calc(var(--sectionSpacing) * -0.5);
        padding-top: 0;
      }
    }
  }

  &[data-site='mep'] {
    --textColour: ${brand.mep.primary};

    &[data-variant='primary'],
    &[data-variant='secondary'] {
      @media ${from(Device.TabletLarge)} {
        ${SectionWrapperStyles.Inner} {
          position: relative;

          &::before {
            position: absolute;
            content: '';
            display: block;
            width: 100%;
            background-repeat: no-repeat;
          }
        }
      }
    }

    &[data-variant='primary'] {
      --bgColour: ${brand.global.red};
      --titleColour: ${brand.global.white};
      --textColour: ${brand.global.white};

      @media ${from(Device.TabletLarge)} {
        ${SectionWrapperStyles.Inner} {
          &::before {
            top: calc(var(--sitePadding) * -1);
            left: 0;
            aspect-ratio: 1920 / 731;
            background-color: ${brand.global.white};
            background-position: top;
          }
        }

        &[data-align='left'] ${SectionWrapperStyles.Inner}:before {
          background-image: url(${svgSwoosh});
        }

        &[data-align='right'] ${SectionWrapperStyles.Inner}:before {
          background-image: url(${svgSwooshReverse});
        }
      }
    }

    &[data-variant='secondary'],
    &[data-variant='tertiary'],
    &[data-variant='full-width'] {
      --titleColour: ${brand.global.red};
    }

    &[data-variant='secondary'],
    &[data-variant='tertiary'] {
      --bgColour: ${brand.mep.secondary.lightGrey};

      ${SectionWrapperStyles.Inner} {
        padding-top: 0;
        padding-bottom: 0;
      }
    }

    &[data-variant='secondary'] {
      @media ${from(Device.TabletLarge)} {
        ${SectionWrapperStyles.Inner} {
          &::before {
            top: 0;
            aspect-ratio: 1141 / 732;
            background-size: 100% 100%;
          }
        }

        &[data-align='left'] ${SectionWrapperStyles.Inner}:before {
          right: -25%;
          background-image: url(${svgCurve});
          background-position: right;
        }

        &[data-align='right'] ${SectionWrapperStyles.Inner}:before {
          left: -25%;
          background-image: url(${svgCurveReverse});
          background-position: left;
        }
      }
    }

    &[data-variant='tertiary'] {
      ${SectionWrapperStyles.Inner} {
        position: relative;
        z-index: 1;
      }

      &::after {
        ${transition('opacity', 3)};

        content: '';
        position: absolute;
        bottom: 0;
        right: 0;
        height: 150%;
        width: 50%;
        border-radius: 100%;
        background: ${brand.mep.secondary.midGrey};
        transform: translate(80%, 55%);
        z-index: 0;
      }

      &[data-align='right'] {
        &::after {
          right: auto;
          left: 0;
          transform: translate(-80%, 55%);
        }
      }

      ${shouldAnimate(css `
        &::after {
          opacity: 0;
        }

        &[data-animate='true'] {
          &::after {
            opacity: 1;
          }
        }
      `)}
    }

    &[data-variant='full-width'] {
      --titleColour: ${brand.mep.primary};
      --textColour: ${brand.mep.primary};
      --bgColour: ${brand.mep.secondary.lightestGrey};
    }
  }

  ${SectionWrapperStyles.Inner} {
    ${baseGrid};

    padding-top: var(--sectionSpacing);
    padding-bottom: var(--sectionSpacing);
  }
`;
const ContentWrapper = styled.div `
  position: relative;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: ${fluid(24, 64)} 0;
  grid-column: 1 / span 6;

  @media ${from(Device.Tablet)} {
    grid-column: 1 / span 8;
    padding-left: calc(var(--gutterWidth) * 2);

    ${Container}[data-animation-variant^='ess-primary'] & {
      padding-bottom: calc(${fluid(24, 64)} + var(--sitePadding));
    }
  }

  @media ${between(Device.TabletLarge, Device.DesktopSmall)} {
    ${Container}[data-animation-variant^='mep-primary'] & {
      padding-top: 90px;
    }
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 1 / span 1;

    ${Container}[data-align='left'] & {
      grid-column: 6 / span 3;
    }

    ${Container}[data-align='right'] & {
      grid-column: 1 / span 3;
    }

    ${Container}[data-variant='full-width'] & {
      grid-column: 1 / span 4;
      position: relative;
      z-index: 1;
    }

    ${Container}[data-animation-variant^='ess-full'] & {
      background-color: ${rgba(brand.ess.secondary.lightGrey, 0.5)};
    }

    ${Container}[data-animation-variant^='mep-full'] & {
      background-color: ${rgba(brand.mep.secondary.lightestGrey, 0.5)};
    }
  }

  @media ${from(Device.Desktop)} {
    padding-left: 0;

    ${Container}[data-align='left'] & {
      grid-column: 9 / span 3;
    }

    ${Container}[data-align='right'] & {
      grid-column: 2 / span 3;
    }

    ${Container}[data-variant='full-width'] & {
      grid-column: 1 / span 4;
    }
  }

  ${Container}[data-animation-variant^='ess-secondary'] & {
    position: relative;
    padding-top: ${fluid(48, 124)};
    padding-bottom: ${fluid(48, 124)};

    &::before,
    &::after {
      ${transition('transform, opacity', 1)};

      content: '';
      position: absolute;
      top: 0;
      height: 100%;
    }

    &::before {
      left: calc(var(--sitePadding) * -1 - 50%);
      width: calc(200% + (var(--sitePadding) * 2));
      background-color: ${brand.ess.secondary.lightestGrey};
      z-index: 2;
    }

    &::after {
      left: calc(var(--sitePadding) * -1);
      width: calc(100% + (var(--sitePadding) * 3));
      background-color: ${brand.ess.secondary.lightGrey};
      z-index: 1;
    }

    @media ${from(Device.TabletLarge)} {
      &::before {
        left: calc((var(--gutterWidth) * -1) + (var(--sitePadding) * -1) - 75%);
        width: calc(250% + (var(--gutterWidth) * 2) + (var(--sitePadding) * 2));
      }

      &::after {
        left: calc((var(--gutterWidth) * -1) + (var(--sitePadding) * -1.5) - 92.5%);
        width: calc(300% + (var(--gutterWidth) * 2) + (var(--sitePadding) * 2));
      }
    }
  }

  ${Container}[data-animation-variant^='ess-secondary-left'] & {
    &::before {
      clip-path: polygon(100% 0%, 100% 100%, 30% 100%, 0% 50%, 30% 0%);
    }

    @media ${from(Device.TabletLarge)} {
      &::before {
        clip-path: polygon(100% 0%, 75% 50%, 100% 100%, ${fluid(220, 280)} 100%, 0% 50%, ${fluid(220, 280)} 0%);
      }

      &::after {
        clip-path: polygon(100% 0%, 100% 100%, ${fluid(220, 280)} 100%, 0% 50%, ${fluid(220, 280)} 0%);
      }
    }
  }

  ${Container}[data-animation-variant^='ess-secondary-right'] & {
    &::before {
      clip-path: polygon(70% 0%, 100% 50%, 70% 100%, 0% 100%, 0% 0%);
    }

    @media ${from(Device.TabletLarge)} {
      &::before {
        clip-path: polygon(calc(100% - ${fluid(220, 280)}) 0%, 100% 50%, calc(100% - ${fluid(220, 280)}) 100%, 0% 100%, 25% 50%, 0% 0%);
      }

      &::after {
        clip-path: polygon(calc(100% - ${fluid(220, 280)}) 0%, 100% 50%, calc(100% - ${fluid(220, 280)}) 100%, 0% 100%, 0% 0%);
      }
    }
  }

  ${Container}[data-variant='tertiary'] & {
    padding-top: calc(${fluid(24, 64)} + var(--sectionSpacing));
    padding-bottom: calc(${fluid(24, 64)} + var(--sectionSpacing));
  }

  ${shouldAnimate(css `
    ${Container}[data-animation-variant='ess-secondary-right-false'] & {
      &::before,
      &::after {
        opacity: 0;
        transform: translateX(-100%);
      }
    }

    ${Container}[data-animation-variant='ess-secondary-left-false'] & {
      &::before,
      &::after {
        opacity: 0;
        transform: translateX(100%);
      }
    }

    ${Container}[data-animation-variant='ess-secondary-right-true'],
    ${Container}[data-animation-variant='ess-secondary-left-true'] & {
      &::before,
      &::after {
        opacity: 1;
        transform: none;
      }
    }
  `)}
`;
const Line = styled.span `
  display: none;

  @media ${from(Device.TabletLarge)} {
    grid-column: 1 / span 8;
    display: block;
    height: 1px;
    background-color: ${brand.ess.secondary.midGrey};
    transform: translateY(calc((${fluid(24, 64)} + var(--sectionSpacing)) * -0.33));
  }

  @media ${from(Device.Desktop)} {
    grid-column: 1 / span 12;
  }
`;
const ImageWrapper = styled.div `
  display: flex;
  align-items: center;
  grid-column: 1 / span 6;

  @media ${until(Device.TabletLarge)} {
    ${Container}[data-animation-variant^='mep-primary'] & {
      order: -1;
    }
  }

  @media ${from(Device.Tablet)} {
    grid-column: 1 / span 8;
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 1 / span 1;

    ${Container}[data-align='left'] & {
      grid-column: 1 / span 5;
    }

    ${Container}[data-align='right'] & {
      grid-column: 4 / span 5;
    }
  }

  @media ${from(Device.Desktop)} {
    ${Container}[data-align='left'] & {
      grid-column: 1 / span 7;
    }

    ${Container}[data-align='right'] & {
      grid-column: 6 / span 7;
    }
  }
`;
const contentInnerAnimations = css `
  ${transition('transform, opacity', 2)};

  position: relative;
  z-index: 3;

  ${shouldAnimate(css `
    ${Container}[data-animation-variant^='ess-secondary'] & {
      opacity: 0;
      transform: translateY(36px);
    }

    ${Container}[data-animation-variant='ess-secondary-right-true'] &,
    ${Container}[data-animation-variant='ess-secondary-left-true'] & {
      opacity: 1;
      transform: none;
    }
  `)}
`;
const Title = styled.h2 `
  ${h2};

  color: var(--titleColour);

  > span {
    display: inline;
    color: var(--highlightColour);
  }

  ${Container}[data-site='mep'] & {
    ${fonts.museoSlab.medium};

    > span {
      ${fonts.daxWide.bold};
    }
  }

  ${contentInnerAnimations};
`;
const Content = styled.div `
  ${contentStyles};

  color: var(--textColour);

  ${contentInnerAnimations};
`;
const CtaWrapper = styled.div `
  margin: ${fluid(26, 40)} 0 0;
  gap: ${fluid(12, 20)};
  display: grid;
  grid-template-columns: max-content;

  @media ${until(Device.MobileLarge)} {
    ${Container}[data-site='ess'] & {
      ${ButtonStyles.Container} {
        max-width: calc(100vw - (var(--sitePadding) * 2) - 48px);
      }
    }

    ${ButtonStyles.Container} {
      max-width: calc(100vw - (var(--sitePadding) * 2));
    }
  }

  @media ${from(Device.TabletLarge)} {
    ${Container}[data-site='mep'] & {
      display: flex;
      flex-wrap: wrap;
      flex-direction: row;
    }
  }

  ${contentInnerAnimations};

  ${Container}[data-animation-variant^='mep-secondary'] &,
  ${Container}[data-animation-variant^='mep-tertiary'] &,
  ${Container}[data-animation-variant^='mep-full-width'] & {
    ${ButtonStyles.Container}[data-type='outline'] {
      --colourText: ${brand.global.red};
    }
  }
`;
const ImageInner = styled.div `
  margin: auto calc(var(--sitePadding) * -1);
  aspect-ratio: 1083 / 653;
  width: calc(100% + (var(--sitePadding) * 2));
  position: relative;

  ${Container}[data-animation-variant^='mep-primary'] & {
    margin-top: 0;
  }

  ${Container}[data-animation-variant^='mep-secondary'] &,
  ${Container}[data-animation-variant^='ess-tertiary'] &,
  ${Container}[data-animation-variant^='mep-tertiary'] & {
    margin-bottom: 0;
  }

  @media ${from(Device.TabletLarge)} {
    margin-left: 0;
    margin-right: 0;

    ${Container}[data-animation-variant^='ess-secondary-left'] &,
    ${Container}[data-animation-variant^='ess-secondary-right'] &,
    ${Container}[data-animation-variant^='mep-secondary-left'] &,
    ${Container}[data-animation-variant^='mep-secondary-right'] & {
      width: calc(100% + var(--sitePadding));
    }

    ${Container}[data-animation-variant^='ess-secondary-left'] &,
    ${Container}[data-animation-variant^='mep-secondary-left'] & {
      margin-left: calc(var(--sitePadding) * -1);
    }

    ${Container}[data-animation-variant^='ess-secondary-right'] &,
    ${Container}[data-animation-variant^='mep-secondary-right'] & {
      margin-right: calc(var(--sitePadding) * -1);
    }

    ${Container}[data-animation-variant^='ess-secondary'] & {
      aspect-ratio: initial;
      height: calc(100% - (var(--sectionSpacing) * 0.5));
      margin-top: calc(var(--sectionSpacing) * 0.5);
    }
  }
`;
const ImageShape = styled.div `
  ${transition('transform, opacity', 1.5)};

  width: 100%;
  height: 100%;
  position: relative;

  ${Container}[data-animation-variant^='ess-primary-left'] & {
    clip-path: polygon(70% 0%, 100% 50%, 70% 100%, 0% 100%, 0% 0%);

    @media ${from(Device.TabletLarge)} {
      clip-path: polygon(70% 0%, 100% 50%, 70% 100%, 0% 100%, 30% 50%, 0% 0%);
    }
  }

  ${Container}[data-animation-variant^='ess-primary-right'] & {
    clip-path: polygon(100% 0%, 100% 100%, 30% 100%, 0% 50%, 30% 0%);

    @media ${from(Device.TabletLarge)} {
      clip-path: polygon(100% 0%, 70% 50%, 100% 100%, 30% 100%, 0% 50%, 30% 0%);
    }
  }

  ${Container}[data-animation-variant^='ess-secondary'] & {
    opacity: 1;
  }

  ${Container}[data-animation-variant^='mep-primary-left'] & {
    clip-path: ellipse(100% 150% at 0% 20%);
    transform: translate(calc(var(--sitePadding) * -1), calc(var(--sectionSpacing) * -1));

    &::before,
    &::after {
      left: -1px;
    }

    &::before {
      clip-path: polygon(0 0, 0% 100%, 100% 100%);
    }
  }

  ${Container}[data-animation-variant^='mep-primary-right'] & {
    clip-path: ellipse(100% 150% at 100% 20%);
    transform: translate(calc(var(--sitePadding) * 1), calc(var(--sectionSpacing) * -1));

    &::before,
    &::after {
      right: -1px;
    }

    &::before {
      clip-path: polygon(100% 0, 0% 100%, 100% 100%);
    }

    &::after {
      transform: rotateY(180deg);
    }
  }

  ${Container}[data-animation-variant^='mep-primary-left'] &,
  ${Container}[data-animation-variant^='mep-primary-right'] & {
    &::before,
    &::after {
      content: '';
      display: block;
      position: absolute;
    }

    &::before {
      width: 85%;
      height: 85%;
      background-color: var(--bgColour);
      z-index: 1;
      bottom: -1px;
    }

    &::after {
      ${transition('transform, opacity', 1)};

      transition-delay: 300ms;
      background-image: url(${mepAdditionalBg});
      background-repeat: no-repeat;
      background-size: contain;
      width: 85%;
      height: 85%;
      bottom: 5%;
      z-index: 2;
    }
  }

  ${shouldAnimate(css `
    opacity: 0;

    ${Container}[data-animation-variant='ess-primary-left-false'] &,
    ${Container}[data-animation-variant='mep-secondary-left-false'] &,
    ${Container}[data-animation-variant='ess-tertiary-left-false'] &,
    ${Container}[data-animation-variant='mep-tertiary-left-false'] & {
      transform: translateX(-100%);
    }

    ${Container}[data-animation-variant='ess-primary-right-false'] &,
    ${Container}[data-animation-variant='mep-secondary-right-false'] &,
    ${Container}[data-animation-variant='ess-tertiary-right-false'] &,
    ${Container}[data-animation-variant='mep-tertiary-right-false'] & {
      transform: translateX(100%);
    }

    ${Container}[data-animation-variant='ess-primary-left-true'] &,
    ${Container}[data-animation-variant='ess-primary-right-true'] &,
    ${Container}[data-animation-variant='mep-secondary-left-true'] &,
    ${Container}[data-animation-variant='mep-secondary-right-true'] &,
    ${Container}[data-animation-variant='ess-tertiary-left-true'] &,
    ${Container}[data-animation-variant='ess-tertiary-right-true'] &,
    ${Container}[data-animation-variant='mep-tertiary-left-true'] &,
    ${Container}[data-animation-variant='mep-tertiary-right-true'] & {
      opacity: 1;
      transform: none;
    }

    ${Container}[data-animation-variant='mep-primary-left-false'] & {
      transform: translate(-100%, calc(var(--sectionSpacing) * -1));

      &::after {
        opacity: 0;
        transform: translateX(-100%);
      }
    }

    ${Container}[data-animation-variant='mep-primary-right-false'] & {
      transform: translate(100%, calc(var(--sectionSpacing) * -1));

      &::after {
        opacity: 0;
        transform: translateX(100%) rotateY(180deg);
      }
    }

    ${Container}[data-animation-variant='mep-primary-left-true'] & {
      opacity: 1;
      transform: translate(calc(var(--sitePadding) * -1), calc(var(--sectionSpacing) * -1));

      &::after {
        opacity: 1;
        transform: none;
      }
    }

    ${Container}[data-animation-variant='mep-primary-right-true'] & {
      opacity: 1;
      transform: translate(calc(var(--sitePadding) * 1), calc(var(--sectionSpacing) * -1));

      &::after {
        opacity: 1;
        transform: rotateY(180deg);
      }
    }
  `)}
`;
const Image = styled.img `
  ${imageCoverContainer()};

  ${Container}[data-animation-variant^='mep-secondary'] & {
    object-position: top center;
  }
`;
const FullWidthImage = styled.div `
  ${transition('opacity', 3)};
  ${scrollbars};

  aspect-ratio: 1787 / 781;
  overflow: auto;
  height: 350px;
  grid-column: 1 / span 6;
  width: 100%;

  @media ${from(Device.Tablet)} {
    grid-column: 1 / span 8;
  }

  @media ${from(Device.TabletLarge)} {
    grid-row: 2 / span 1;
    margin-top: calc(${fluid(128, 256, Device.TabletLarge)} * -1);
    height: auto;
  }

  @media ${from(Device.Desktop)} {
    grid-column: 1 / span 12;
  }

  > img {
    display: block;
    max-width: none;
    height: 100%;

    @media ${from(Device.TabletLarge)} {
      height: auto;
      width: 100%;
      max-width: 100%;
    }
  }

  ${shouldAnimate(css `
    opacity: 0;

    ${Container}[data-animate='true'] & {
      opacity: 1;
    }
  `)}
`;
const ChevronWrapper = styled.div `
  display: none;

  @media ${from(Device.TabletLarge)} {
    ${transition('opacity', 3)}

    display: block;
    position: absolute;
    top: 50%;
    left: 0;
    width: 100%;
    z-index: 1;
    height: 66%;

    ${Container}[data-animation-variant^='ess-primary'] & {
      color: ${brand.global.red};
      transform: translate(calc(var(--sitePadding) * -2), -50%);

      &::before {
        background: ${brand.ess.secondary.midGrey};
        clip-path: polygon(0 0, 0% 100%, 100% 50%);
        content: '';
        position: absolute;
        top: 50%;
        left: 0%;
        height: 55%;
        transform: translateY(-50%);
        aspect-ratio: 1 / 2;
      }
    }

    ${Container}[data-animation-variant^='ess-tertiary'] & {
      color: ${brand.ess.secondary.lightGrey};
    }

    ${Container}[data-animation-variant^='ess-primary-right'] & {
      transform: translate(calc(var(--sitePadding) * 2), -50%) rotate(180deg);
    }

    ${Container}[data-animation-variant^='ess-tertiary-left'] & {
      transform: translate(calc(90% + var(--sitePadding) * 1.5), -50%) rotate(180deg);
    }

    ${Container}[data-animation-variant^='ess-tertiary-right'] & {
      transform: translate(calc(-90% + var(--sitePadding) * -1.5), -50%);
    }

    > svg {
      display: block;
      height: 100%;
    }

    ${shouldAnimate(css `
      opacity: 0;

      ${Container}[data-animate='true'] & {
        opacity: 1;
      }
    `)}
  }

  @media ${from(Device.DesktopLarge)} {
    ${Container}[data-animation-variant^='ess-tertiary-left'] & {
      transform: translate(calc(75% + var(--sitePadding) * 1.5), -50%) rotate(180deg);
    }

    ${Container}[data-animation-variant^='ess-tertiary-right'] & {
      transform: translate(calc(-75% + var(--sitePadding) * -1.5), -50%);
    }
  }
`;
const CtaBannerStyles = {
    ChevronWrapper,
    Container,
    Content,
    ContentWrapper,
    CtaWrapper,
    FullWidthImage,
    Image,
    ImageInner,
    ImageShape,
    ImageWrapper,
    Line,
    Title,
};
export default CtaBannerStyles;
